import { useEffect } from "react";
import { toast } from "@/lib/toast";

const CHECK_INTERVAL_MS = 10 * 60_000;
const TOAST_ID = "new-version-available";

/** Fetch the deployed build id (git tree hash of frontend/, so docs- and
 * backend-only pushes keep the same id). Null on any failure (offline, dev
 * server SPA-fallback HTML, a deploy mid-flight) — a failed check never
 * prompts. */
export async function fetchDeployedBuild(fetchFn: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchFn(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const build = (body as { build?: unknown } | null)?.build;
    return typeof build === "string" && build && build !== "unknown" ? build : null;
  } catch {
    return null;
  }
}

/** A tab keeps running the bundle it loaded until a reload, so a frontend
 * ship never reaches open tabs on its own. Check the deployed build on focus /
 * visibility and every 10 min; on mismatch show a persistent toast. Never
 * auto-reload — unsaved Write drafts and in-flight trawls live in the tab. */
export function useNewVersionPrompt(
  currentBuild: string = import.meta.env.VITE_BUILD_ID,
  enabled: boolean = import.meta.env.PROD,
) {
  useEffect(() => {
    if (!enabled || !currentBuild || currentBuild === "unknown") return;
    let promptedFor: string | null = null;
    let inFlight = false;

    const check = async () => {
      if (inFlight || document.visibilityState !== "visible") return;
      inFlight = true;
      const deployed = await fetchDeployedBuild();
      inFlight = false;
      if (!deployed || deployed === currentBuild || deployed === promptedFor) return;
      promptedFor = deployed;
      toast.info("A new version of PaperChase is available.", {
        id: TOAST_ID,
        duration: Infinity,
        action: { label: "Reload", onClick: () => window.location.reload() },
      });
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(interval);
    };
  }, [currentBuild, enabled]);
}
